import React, { useState, useEffect } from 'react';
import { Building2, ArrowRight, ArrowLeft } from 'lucide-react';
import { CertifiedNetworkApplication, ITALIAN_PROVINCES } from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step2StudioDataProps {
  data: Partial<CertifiedNetworkApplication>;
  associateData: any;
  onComplete: (data: any) => void;
  onBack: () => void;
}

const Step2StudioData: React.FC<Step2StudioDataProps> = ({
  data,
  associateData,
  onComplete,
  onBack
}) => {
  // Debug: verifica dati ricevuti
  console.log('Step2StudioData - associateData:', associateData);
  console.log('Step2StudioData - associateData.rappresentante:', associateData?.rappresentante);
  console.log('Step2StudioData - associateData.codiceFiscale:', associateData?.codiceFiscale);
  console.log('Step2StudioData - associateData.codice:', associateData?.codice);
  console.log('Step2StudioData - data:', data);

  // Usa i campi dedicati se disponibili, altrimenti fallback allo split
  const getRappresentanteFields = () => {
    if (associateData?.rappresentanteNome || associateData?.rappresentanteCognome) {
      return { cognome: associateData.rappresentanteCognome || '', nome: associateData.rappresentanteNome || '' };
    }
    const fullName = associateData?.rappresentante || '';
    if (!fullName) return { cognome: '', nome: '' };
    const parts = fullName.trim().split(/\s+/);
    if (parts.length <= 1) return { cognome: parts[0] || '', nome: '' };
    return { cognome: parts[0], nome: parts.slice(1).join(' ') };
  };

  const { cognome: rappresentanteCognome, nome: rappresentanteNome } = getRappresentanteFields();

  console.log('Step2StudioData - rappresentante split:', { cognome: rappresentanteCognome, nome: rappresentanteNome });

  const initialFormData = {
    studioData: {
      ragioneSociale: data.studioData?.ragioneSociale || associateData?.nominativo || '',
      partitaIVA: data.studioData?.partitaIVA || associateData?.partitaIVA || '',
      codiceFiscale: data.studioData?.codiceFiscale || associateData?.codiceFiscale || '',
      sedeLegale: data.studioData?.sedeLegale || {
        indirizzo: '',
        citta: '',
        provincia: '',
        cap: ''
      },
      sedeOperativa: data.studioData?.sedeOperativa || {
        indirizzo: '',
        citta: '',
        provincia: '',
        cap: ''
      },
      telefono: data.studioData?.telefono || associateData?.telefono || '',
      email: data.studioData?.email || associateData?.email || '',
      sitoWeb: data.studioData?.sitoWeb || '',
      numeroDipendenti: data.studioData?.numeroDipendenti || 0,
      totaleAddetti: data.studioData?.totaleAddetti || 0
    },
    legalRepresentative: {
      nome: data.legalRepresentative?.nome || rappresentanteNome,
      cognome: data.legalRepresentative?.cognome || rappresentanteCognome,
      codiceFiscale: data.legalRepresentative?.codiceFiscale || associateData?.codiceFiscale || '',
      ruolo: data.legalRepresentative?.ruolo || '',
      numeroTesseraUNASCA: data.legalRepresentative?.numeroTesseraUNASCA || associateData?.codice || ''
    },
    sermetra: data.sermetra || {
      isConsorziato: false,
      numeroConsorziato: ''
    }
  };

  console.log('Step2StudioData - initialFormData:', initialFormData);

  const [formData, setFormData] = useState(initialFormData);
  const [sameAddress, setSameAddress] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [showInfoModal, setShowInfoModal] = useState(true);

  // Copia indirizzo sede legale in sede operativa
  useEffect(() => {
    if (sameAddress) {
      setFormData(prev => ({
        ...prev,
        studioData: {
          ...prev.studioData,
          sedeOperativa: { ...prev.studioData.sedeLegale }
        }
      }));
    }
  }, [sameAddress, formData.studioData.sedeLegale]);

  const handleChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
    // Rimuovi errore quando l'utente modifica il campo
    if (errors[`${section}.${field}`]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  const handleNestedChange = (section: string, subsection: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [subsection]: {
          ...(prev[section as keyof typeof prev] as any)[subsection],
          [field]: value
        }
      }
    }));
  };

  const validate = () => {
    const newErrors: any = {};

    // Validazione Studio Data
    if (!formData.studioData.ragioneSociale.trim()) {
      newErrors['studioData.ragioneSociale'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.partitaIVA.trim()) {
      newErrors['studioData.partitaIVA'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.email.trim()) {
      newErrors['studioData.email'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.sedeLegale.indirizzo.trim()) {
      newErrors['studioData.sedeLegale.indirizzo'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.sedeLegale.citta.trim()) {
      newErrors['studioData.sedeLegale.citta'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.sedeLegale.provincia) {
      newErrors['studioData.sedeLegale.provincia'] = 'Campo obbligatorio';
    }
    if (!formData.studioData.sedeLegale.cap.trim()) {
      newErrors['studioData.sedeLegale.cap'] = 'Campo obbligatorio';
    }

    // Validazione Legale Rappresentante
    if (!formData.legalRepresentative.nome.trim()) {
      newErrors['legalRepresentative.nome'] = 'Campo obbligatorio';
    }
    if (!formData.legalRepresentative.cognome.trim()) {
      newErrors['legalRepresentative.cognome'] = 'Campo obbligatorio';
    }
    if (!formData.legalRepresentative.codiceFiscale.trim()) {
      newErrors['legalRepresentative.codiceFiscale'] = 'Campo obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onComplete(formData);
    }
  };

  return (
    <>
      {/* Modale Informativo */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full">
            {/* Content */}
            <div className="p-4 space-y-1">
              <div className="text-smbg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                  Campi Pre-compilati
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Per comodità, abbiamo già compilato molti campi utilizzando i dati presenti nella tua anagrafica UNASCA.
                  <strong className="block mt-2">Ti chiediamo di verificare attentamente tutti i dati e di correggerli se necessario.</strong>
                </p>
              </div>

              <div className="text-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
                  Compilazione Non Obbligatoria
                </h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Se in questa fase non sei a conoscenza di qualche informazione, puoi comunque procedere con l'invio della domanda.
                  Il sistema <strong>non richiede la compilazione obbligatoria di tutti i campi</strong>.
                </p>
                <p className="text-sm text-green-800 dark:text-green-200 mt-2">
                  Potrai sempre integrare le informazioni mancanti in un secondo momento.
                </p>
              </div>

              <div className="text-smbg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                  Campi Obbligatori
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  I campi contrassegnati con <span className="text-red-600 font-bold">*</span> sono obbligatori e devono essere compilati per procedere.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => setShowInfoModal(false)}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Ho Capito, Procedi
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-full mb-4">
            <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Dati dello Studio
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Inserisci i dati completi dello studio, del legale rappresentante e del consorzio Sermetra.
          </p>
        </div>

      {/* Sezione 1: Dati Studio */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          1. Dati dello Studio di Consulenza Automobilistica
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ragione Sociale *
            </label>
            <input
              type="text"
              value={formData.studioData.ragioneSociale}
              onChange={(e) => handleChange('studioData', 'ragioneSociale', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
            {errors['studioData.ragioneSociale'] && (
              <p className="mt-1 text-sm text-red-600">{errors['studioData.ragioneSociale']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Partita IVA *
            </label>
            <input
              type="text"
              value={formData.studioData.partitaIVA}
              onChange={(e) => handleChange('studioData', 'partitaIVA', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Codice Fiscale
            </label>
            <input
              type="text"
              value={formData.studioData.codiceFiscale}
              onChange={(e) => handleChange('studioData', 'codiceFiscale', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Telefono *
            </label>
            <input
              type="tel"
              value={formData.studioData.telefono}
              onChange={(e) => handleChange('studioData', 'telefono', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.studioData.email}
              onChange={(e) => handleChange('studioData', 'email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
            {errors['studioData.email'] && (
              <p className="mt-1 text-sm text-red-600">{errors['studioData.email']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sito Web
            </label>
            <input
              type="url"
              value={formData.studioData.sitoWeb}
              onChange={(e) => handleChange('studioData', 'sitoWeb', e.target.value)}
              placeholder="https://"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Numero Dipendenti
            </label>
            <input
              type="number"
              min="0"
              value={formData.studioData.numeroDipendenti}
              onChange={(e) => handleChange('studioData', 'numeroDipendenti', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Totale Addetti
            </label>
            <input
              type="number"
              min="0"
              value={formData.studioData.totaleAddetti}
              onChange={(e) => handleChange('studioData', 'totaleAddetti', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Sede Legale */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">Sede Legale</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Indirizzo *
              </label>
              <input
                type="text"
                value={formData.studioData.sedeLegale.indirizzo}
                onChange={(e) => handleNestedChange('studioData', 'sedeLegale', 'indirizzo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
              {errors['studioData.sedeLegale.indirizzo'] && (
                <p className="mt-1 text-sm text-red-600">{errors['studioData.sedeLegale.indirizzo']}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Città *
              </label>
              <input
                type="text"
                value={formData.studioData.sedeLegale.citta}
                onChange={(e) => handleNestedChange('studioData', 'sedeLegale', 'citta', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Provincia *
              </label>
              <select
                value={formData.studioData.sedeLegale.provincia}
                onChange={(e) => handleNestedChange('studioData', 'sedeLegale', 'provincia', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              >
                <option value="">Seleziona...</option>
                {ITALIAN_PROVINCES.map(prov => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                CAP *
              </label>
              <input
                type="text"
                maxLength={5}
                value={formData.studioData.sedeLegale.cap}
                onChange={(e) => handleNestedChange('studioData', 'sedeLegale', 'cap', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Checkbox Stesso Indirizzo */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="sameAddress"
            checked={sameAddress}
            onChange={(e) => setSameAddress(e.target.checked)}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="sameAddress" className="text-sm text-gray-700 dark:text-gray-300">
            La sede operativa coincide con la sede legale
          </label>
        </div>

        {/* Sede Operativa */}
        {!sameAddress && (
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">Sede Operativa</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Indirizzo
                </label>
                <input
                  type="text"
                  value={formData.studioData.sedeOperativa.indirizzo}
                  onChange={(e) => handleNestedChange('studioData', 'sedeOperativa', 'indirizzo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Città
                </label>
                <input
                  type="text"
                  value={formData.studioData.sedeOperativa.citta}
                  onChange={(e) => handleNestedChange('studioData', 'sedeOperativa', 'citta', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Provincia
                </label>
                <select
                  value={formData.studioData.sedeOperativa.provincia}
                  onChange={(e) => handleNestedChange('studioData', 'sedeOperativa', 'provincia', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Seleziona...</option>
                  {ITALIAN_PROVINCES.map(prov => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CAP
                </label>
                <input
                  type="text"
                  maxLength={5}
                  value={formData.studioData.sedeOperativa.cap}
                  onChange={(e) => handleNestedChange('studioData', 'sedeOperativa', 'cap', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sezione 2: Legale Rappresentante */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          2. Dati del Legale Rappresentante
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome *
            </label>
            <input
              type="text"
              value={formData.legalRepresentative.nome}
              onChange={(e) => handleChange('legalRepresentative', 'nome', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
            {errors['legalRepresentative.nome'] && (
              <p className="mt-1 text-sm text-red-600">{errors['legalRepresentative.nome']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cognome *
            </label>
            <input
              type="text"
              value={formData.legalRepresentative.cognome}
              onChange={(e) => handleChange('legalRepresentative', 'cognome', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
            {errors['legalRepresentative.cognome'] && (
              <p className="mt-1 text-sm text-red-600">{errors['legalRepresentative.cognome']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Codice Fiscale *
            </label>
            <input
              type="text"
              maxLength={16}
              value={formData.legalRepresentative.codiceFiscale}
              onChange={(e) => handleChange('legalRepresentative', 'codiceFiscale', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
            {errors['legalRepresentative.codiceFiscale'] && (
              <p className="mt-1 text-sm text-red-600">{errors['legalRepresentative.codiceFiscale']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ruolo
            </label>
            <input
              type="text"
              value={formData.legalRepresentative.ruolo}
              onChange={(e) => handleChange('legalRepresentative', 'ruolo', e.target.value)}
              placeholder="es. Titolare, Amministratore"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Numero Tessera UNASCA
            </label>
            <input
              type="text"
              value={formData.legalRepresentative.numeroTesseraUNASCA}
              onChange={(e) => handleChange('legalRepresentative', 'numeroTesseraUNASCA', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      {/* Sezione 3: Consorzio Sermetra */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          3. Consorzio Sermetra
        </h3>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isConsorziato"
              checked={formData.sermetra.isConsorziato}
              onChange={(e) => handleChange('sermetra', 'isConsorziato', e.target.checked)}
              className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <label htmlFor="isConsorziato" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Lo Studio è consorziato Sermetra
            </label>
          </div>

          {formData.sermetra.isConsorziato && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Numero Consorziato
              </label>
              <input
                type="text"
                value={formData.sermetra.numeroConsorziato}
                onChange={(e) => handleChange('sermetra', 'numeroConsorziato', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          type="submit"
          className="flex items-center gap-2 bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </LoadingButton>
      </div>
    </form>
    </>
  );
};

export default Step2StudioData;

